import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import { useAbility } from 'utilities/ability'
import { isRecoveryConfigured } from '../recoveryStatus'
import { ACTIVATION_PATH, WIZARD_QUICK_START_PATH } from '../routes'

// Where the promo page's single "Activate RecoveryAI" CTA sends the merchant.
// Both CTAs (Hero + the closing CtaSection) read this one hook so they can never
// split-brain onto different targets.
//
// A configured-but-off merchant (onboarding_completed true, operating_mode 'off')
// has ALREADY finished the wizard — pushing them back into quick-start would walk
// them through setup they completed. The pre-promo AI-693 flow sent exactly that
// state to ACTIVATION_PATH (the pricing/activate CTA), and this preserves it now
// that the promo page sits in front of every !isActive merchant. Everyone else
// (no config, or a half-finished wizard) starts at the wizard's quick-start.
//
// `isRecoveryConfigured` is the shared recoveryStatus predicate (same source as
// Landing's gate and the Wizard re-entry guard) so the derivation can't drift.
const usePromoCtaTarget = (): string => {
  // The config GET is `rbac_authorize!('view', 'merchant_settings')` server-side,
  // and api/index.ts raises a danger toast for every RBAC 403 — so this request
  // must never leave the browser for a viewer without the permission. It used to
  // ride along on Landing's own config fetch, but Landing now skips that query
  // for exactly these users, which makes THIS hook the one that issues it.
  //
  // Skipped ⇒ `config` undefined ⇒ `isRecoveryConfigured` false ⇒ the target
  // falls back to quick-start. Harmless: both CTAs that read this (Hero and
  // CtaSection) are already disabled via their own `cannotManage` check for a
  // viewer without MerchantSettings, so the target is never navigated to.
  const ability = useAbility()
  const canAccessConsole = ability.can('access', 'MerchantSettings')
  const { data: config } = useGetRecoveryConfigQuery(undefined, {
    skip: !canAccessConsole,
  })

  return isRecoveryConfigured(config)
    ? ACTIVATION_PATH
    : WIZARD_QUICK_START_PATH
}

export default usePromoCtaTarget
